import { useState } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { darken, lighten, alpha } from '@material-ui/core/styles/colorManipulator';
import { t } from '@lingui/macro';
import clsx from 'clsx';
import { find, isEmpty, reject } from 'lodash';
import ViewColumn from '@material-ui/icons/ViewColumn';
import { IconButton, Popover, FormControlLabel, Checkbox, Tooltip } from '@material-ui/core';
import { useColumns } from '../ColumnContext';
import { useSetTableColumnOverrides, useTableColumnOverrides } from '../TableState';
import useIsPrinting from '../../../utilities/PrintUtilities';

const useStyles = makeStyles((theme) => ({
    buttonContainer: {
        '& .MuiIconButton-root:hover': {
            background: 'none',
        },
    },
    actionMenu: {
        padding: 0,
        maxHeight: 280,
        'flex-wrap': 'nowrap',
        'list-style': 'none',
        '& li': {
            cursor: 'pointer',
            '&:hover': {
                backgroundColor:
                    theme.palette.type === 'light'
                        ? darken(alpha(theme.palette.background.paper, 1), 0.03)
                        : lighten(alpha(theme.palette.background.paper, 1), 0.06),
            },
        },
    },
    selectionItem: {
        padding: theme.spacing(1, 2),
        margin: 0,
        width: '100%',
    },
}));

export default function ColumnRemover() {
    const [menuAnchor, setMenuAnchor] = useState(null);
    const classes = useStyles();
    const columnOverrides = useTableColumnOverrides();
    const setColumnOverrides = useSetTableColumnOverrides();
    const columns = useColumns(true);
    const isPrinting = useIsPrinting();

    const anchorOrigin = {
        vertical: 'bottom',
        horizontal: 'right',
    };

    const transformOrigin = {
        vertical: 'top',
        horizontal: 'left',
    };

    let selectableColumns = [];
    selectableColumns = buildSelectableColumns(columns, columnOverrides);

    function handleOpenMenu(event) {
        setMenuAnchor(event.currentTarget);
    }

    function handleCloseMenu() {
        setMenuAnchor(null);
    }

    function handleHideColumn(value, column) {
        if (value) {
            return setColumnOverrides([
                ...reject(columnOverrides, (c) => c.id === column.id || c.assocId === column.id),
            ]);
        }
        return setColumnOverrides([...reject(columnOverrides, { id: column.id }), { id: column.id, hidden: true }]);
    }

    function buildSelectableColumns(cols, overrides) {
        const selectableCols = [];
        cols.forEach((column) => {
            const columnOverride = find(overrides, { id: column.id });
            if (column.title && !column.assocId && column.removable !== false)
                selectableCols.push({
                    id: column.id,
                    title: column.group ? `${column.group} ${column.title}` : column.title,
                    removable: column.removable,
                    hidden: columnOverride?.hidden || false,
                });
        });
        return selectableCols;
    }

    if (isPrinting || isEmpty(selectableColumns)) {
        return null;
    }

    return (
        <>
            <Tooltip title={t`Add/Remove Columns`}>
                <span className={classes.buttonContainer}>
                    <IconButton
                        className={clsx({ [classes.activeAction]: menuAnchor !== null })}
                        onClick={handleOpenMenu}
                        size="small"
                    >
                        <ViewColumn />
                    </IconButton>
                </span>
            </Tooltip>
            <Popover
                anchorEl={menuAnchor}
                anchorOrigin={anchorOrigin}
                transformOrigin={transformOrigin}
                open={menuAnchor !== null}
                onClose={handleCloseMenu}
            >
                <ul className={classes.actionMenu}>
                    {selectableColumns.map((column) => (
                        <li key={column.id}>
                            <FormControlLabel
                                className={classes.selectionItem}
                                control={
                                    <Checkbox
                                        checked={!column.hidden || column.removable === false}
                                        disabled={column.removable === false}
                                        color="default"
                                        size="small"
                                    />
                                }
                                onChange={(event) => handleHideColumn(event.target.checked, column)}
                                label={column.title}
                            />
                        </li>
                    ))}
                </ul>
            </Popover>
        </>
    );
}
